import React, { FC, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import LoopIcon from '@material-ui/icons/Loop';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckIcon from '@material-ui/icons/Check';
import { yupResolver } from '@hookform/resolvers';
import { useDispatch, useSelector } from 'react-redux';
import schema from './Schema';
import ProjectControlsArea from './ProjectControlsArea';
import ReviewStep from './ReviewStep';
import { GET_ALL_QUERY_PARAMS } from '../../../../../../constants';
import { getVocabulariesDataList } from '../../../../../../api/vocabulary';
import { setShowDrawerContent, setShowNewProjectPopup, setVocabulary } from '../../../../../../redux/slices/generalSlice';
import { RootState } from '../../../../../../redux/root';
import useGetNewProjectData from '../../../../hooks/useGetNewProjectData';
import { persistNewProject } from '../../../../utils';

type FormValue = {
  address: string;
  street: string;
  city: string;
  state: string;
  zipCode: string;
  contact: string;
  phone: string;
  projectName: string;
  description: string;
  serviceType: string;
  applicantType: string;
  documentFile: any;
  franchise: any;
  manager: any;
};

type Props = {
  setShowModal: Function;
};

type StepDef = {
  id: number;
  label: string;
  shortLabel: string;
  fields: (keyof FormValue)[];
};

const STEPS: StepDef[] = [
  { id: 1, label: 'Location', shortLabel: 'Location', fields: ['address', 'street', 'city', 'state', 'zipCode'] },
  { id: 2, label: 'Details', shortLabel: 'Details', fields: ['projectName', 'description'] },
  { id: 3, label: 'Services', shortLabel: 'Services', fields: ['serviceType'] },
  { id: 4, label: 'Scope', shortLabel: 'Scope', fields: ['applicantType', 'work' as any, 'building' as any] },
  { id: 5, label: 'Office', shortLabel: 'Office', fields: ['franchise'] },
  { id: 6, label: 'Files', shortLabel: 'Files', fields: [] },
  { id: 7, label: 'Review', shortLabel: 'Review', fields: [] },
];

const TOTAL_STEPS = STEPS.length;

const NewProject: FC<Props> = ({ setShowModal }) => {
  const {
    handleSubmit,
    control,
    register,
    errors,
    trigger,
    setValue,
    getValues,
    clearErrors,
    reset,
  } = useForm<FormValue>({
    resolver: yupResolver(schema),
    defaultValues: {} as FormValue,
  });

  const { loadProjects } = useGetNewProjectData({ projects: true });
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const formRef = useRef<HTMLFormElement>(null);
  const disableSave = useSelector((s: RootState) => s.projectsSlice.disableSave);
  const vocabularies = useSelector((state: RootState) => state.general.vocabulary);

  useEffect(() => {
    const loadVocabulary = async () => {
      const payload = await getVocabulariesDataList({
        ...GET_ALL_QUERY_PARAMS,
        ordering: 'title',
      });
      const vocabularyItems = payload.data.results;
      dispatch(setVocabulary(vocabularyItems));
    };
    if (vocabularies.length === 0) {
      loadVocabulary();
    }
  }, []);

  const onSubmit = async data => {
    setIsLoading(true);
    const result = await persistNewProject(data, vocabularies);
    // @ts-ignore
    if (result.status === 201) {
      loadProjects(vocabularies, true);
      setShowModal(true);
      setIsLoading(false);
      dispatch(setShowDrawerContent(false));
      dispatch(setShowNewProjectPopup(false));
    } else {
      setIsLoading(false);
    }
  };

  const onCancel = () => {
    dispatch(setShowDrawerContent(false));
    dispatch(setShowNewProjectPopup(false));
  };

  const goToStep = (step: number) => {
    if (step < 1 || step > TOTAL_STEPS) return;
    setCurrentStep(step);
    requestAnimationFrame(() => {
      let node: HTMLElement | null = formRef.current;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        if (/(auto|scroll)/.test(style.overflowY)) {
          node.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        }
        node = node.parentElement;
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    });
  };

  const onNext = async () => {
    const { fields } = STEPS[currentStep - 1];
    const isValid = fields.length === 0 ? true : await trigger(fields as any);
    if (isValid) {
      goToStep(currentStep + 1);
    }
  };

  const onBack = () => goToStep(currentStep - 1);

  const handleEditFromReview = (step: number) => goToStep(step);

  const isLast = currentStep === TOTAL_STEPS;
  const stepDef = STEPS[currentStep - 1];
  const progressPct = (currentStep / TOTAL_STEPS) * 100;

  return (
    <Form ref={formRef} onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <Stepper aria-label="Progress">
        <StepperBar>
          {STEPS.map(s => {
            let state: 'done' | 'active' | 'todo' = 'todo';
            if (s.id < currentStep) state = 'done';
            else if (s.id === currentStep) state = 'active';
            const clickable = s.id < currentStep;
            return (
              <StepNode
                key={s.id}
                type="button"
                $state={state}
                onClick={() => clickable && goToStep(s.id)}
                disabled={!clickable && s.id !== currentStep}
                aria-current={s.id === currentStep ? 'step' : undefined}
              >
                <StepCircle $state={state}>
                  {state === 'done' ? <CheckIcon style={{ fontSize: 16 }} /> : s.id}
                </StepCircle>
                <StepLabel $state={state}>{s.shortLabel}</StepLabel>
              </StepNode>
            );
          })}
        </StepperBar>

        <StepperMobile>
          <MobileMeta>
            <MobileEyebrow>Step {currentStep} of {TOTAL_STEPS}</MobileEyebrow>
            <MobileTitle>{stepDef.label}</MobileTitle>
          </MobileMeta>
          <ProgressTrack>
            <ProgressFill style={{ width: `${progressPct}%` }} />
          </ProgressTrack>
        </StepperMobile>
      </Stepper>

      <FormBody id="np-wizard-body">
        <FieldsHost $hidden={isLast}>
          <ProjectControlsArea
            register={register}
            errors={errors}
            control={control}
            setValue={setValue}
            trigger={trigger}
            getValues={getValues}
            clearErrors={clearErrors}
            reset={reset}
            currentStep={currentStep}
          />
        </FieldsHost>
        {isLast && (
          <ReviewStep values={getValues()} onEditStep={handleEditFromReview} />
        )}
      </FormBody>

      <Footer>
        <FooterNote>You can update project details later from your dashboard.</FooterNote>
        <FooterButtons>
          <CancelBtn type="button" onClick={onCancel} disabled={isLoading}>
            Cancel
          </CancelBtn>
          {currentStep > 1 && (
            <BackBtn type="button" onClick={onBack} disabled={isLoading}>
              <ArrowBackIcon style={{ fontSize: 18 }} />
              Back
            </BackBtn>
          )}
          {!isLast && (
            <NextBtn type="button" onClick={onNext} disabled={isLoading}>
              Next
              <ArrowForwardIcon style={{ fontSize: 18 }} />
            </NextBtn>
          )}
          {isLast && (
            <SubmitBtn type="submit" disabled={disableSave || isLoading}>
              {isLoading ? (
                <>
                  Submitting
                  <SpinIcon>
                    <LoopIcon />
                  </SpinIcon>
                </>
              ) : (
                'Submit Request'
              )}
            </SubmitBtn>
          )}
        </FooterButtons>
      </Footer>
    </Form>
  );
};

export default NewProject;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont,
    'Helvetica Neue', Arial, Roboto, sans-serif,
    'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol';

  .MuiFormControl-root {
    margin-top: 0 !important;
  }

  .MuiFormLabel-root,
  .MuiInputLabel-outlined {
    position: static !important;
    transform: none !important;
    font-family: inherit !important;
    font-size: 0.875rem !important;
    font-weight: 600 !important;
    color: var(--mainText) !important;
    margin-bottom: 6px !important;
    pointer-events: auto !important;
    max-width: none !important;
  }

  .MuiFormLabel-asterisk {
    color: var(--danger-500, #dc2626) !important;
    margin-left: 2px !important;
  }

  .MuiInputLabel-outlined.MuiInputLabel-shrink {
    transform: none !important;
  }

  .MuiOutlinedInput-root {
    border-radius: 8px !important;
    background: var(--neutral-0) !important;
    font-family: inherit !important;
    font-size: 0.875rem !important;
    color: var(--mainText) !important;
  }

  .MuiOutlinedInput-input {
    padding: 10px 12px !important;
    height: auto !important;
    min-height: 20px !important;
  }

  .MuiOutlinedInput-multiline {
    padding: 0 !important;
  }

  .MuiOutlinedInput-inputMultiline {
    padding: 10px 12px !important;
  }

  .MuiOutlinedInput-notchedOutline {
    border: 1px solid var(--border) !important;
    top: 0 !important;
  }

  .MuiOutlinedInput-notchedOutline legend {
    display: none !important;
  }

  .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline {
    border-color: var(--neutral-400) !important;
  }

  .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: 1px solid var(--primary-600) !important;
    box-shadow: var(--shadow-focus);
  }

  .MuiOutlinedInput-root.Mui-disabled {
    background: var(--neutral-100) !important;
  }

  .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline {
    border-color: var(--danger-500, #dc2626) !important;
  }

  .MuiFormHelperText-root.Mui-error {
    font-family: inherit !important;
    font-size: 0.75rem !important;
    color: var(--danger-500, #dc2626) !important;
    margin-left: 2px !important;
  }

  .MuiFormControl-root,
  .MuiTextField-root {
    display: flex !important;
    flex-direction: column !important;
  }
`;

const Stepper = styled.div`
  position: sticky;
  top: -35px;
  margin: -35px -35px 18px;
  padding: 16px 28px 14px;
  background: var(--neutral-0);
  border-bottom: 1px solid var(--border);
  z-index: 4;

  @media only screen and (max-width: 840px) {
    top: -22px;
    margin: -22px -22px 14px;
    padding: 14px 18px 12px;
  }

  @media only screen and (max-width: 540px) {
    top: -16px;
    margin: -16px -14px 12px;
    padding: 12px 14px 10px;
  }
`;

const StepperBar = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 14px;
    left: calc((100% / 7) / 2);
    right: calc((100% / 7) / 2);
    height: 2px;
    background: var(--border);
    z-index: 0;
  }

  @media only screen and (max-width: 640px) {
    display: none;
  }
`;

const StepNode = styled.button<{ $state: StepState }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0 4px;
  background: transparent;
  border: none;
  cursor: ${({ $state }) => ($state === 'done' ? 'pointer' : 'default')};
  font-family: inherit;

  &:focus-visible {
    outline: none;
  }

  &:focus-visible > span:first-child {
    box-shadow: var(--shadow-focus);
  }
`;

type StepState = 'done' | 'active' | 'todo';

const circleBg = ($state: StepState) => {
  if ($state === 'done') return 'var(--success-600, #16a34a)';
  if ($state === 'active') return 'var(--primary-600)';
  return 'var(--neutral-0)';
};

const circleColor = ($state: StepState) => {
  if ($state === 'done') return 'var(--neutral-0)';
  if ($state === 'active') return 'var(--neutral-0)';
  return 'var(--neutral-400)';
};

const circleBorder = ($state: StepState) => {
  if ($state === 'done') return 'var(--success-600, #16a34a)';
  if ($state === 'active') return 'var(--primary-600)';
  return 'var(--border)';
};

const labelColor = ($state: StepState) => {
  if ($state === 'active') return 'var(--primary-700, #0b5a87)';
  if ($state === 'done') return 'var(--success-700, #15803d)';
  return 'var(--neutral-500)';
};

const StepCircle = styled.span<{ $state: StepState }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 0.8125rem;
  font-weight: 700;
  border: 2px solid ${({ $state }) => circleBorder($state)};
  background: ${({ $state }) => circleBg($state)};
  color: ${({ $state }) => circleColor($state)};
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;
`;

const StepLabel = styled.span<{ $state: StepState }>`
  font-size: 0.75rem;
  font-weight: ${({ $state }) => ($state === 'active' ? 700 : 600)};
  color: ${({ $state }) => labelColor($state)};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;

  @media only screen and (max-width: 840px) {
    font-size: 0.6875rem;
  }
`;

const StepperMobile = styled.div`
  display: none;
  flex-direction: column;
  gap: 8px;

  @media only screen and (max-width: 640px) {
    display: flex;
  }
`;

const MobileMeta = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 10px;
`;

const MobileEyebrow = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--primary-600);
`;

const MobileTitle = styled.span`
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--mainText);
`;

const ProgressTrack = styled.div`
  width: 100%;
  height: 6px;
  background: var(--neutral-100);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: var(--primary-600);
  border-radius: 999px;
  transition: width 0.25s ease;
`;

const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-bottom: 88px;
`;

const FieldsHost = styled.div<{ $hidden: boolean }>`
  display: ${({ $hidden }) => ($hidden ? 'none' : 'flex')};
  flex-direction: column;
  gap: 14px;
  min-width: 0;
`;

const Footer = styled.div`
  position: sticky;
  bottom: -35px;
  margin: 0 -35px -35px;
  padding: 14px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: var(--neutral-0);
  border-top: 1px solid var(--border);
  box-shadow: 0 -6px 16px rgba(15, 23, 42, 0.08);
  z-index: 5;
  flex-wrap: wrap;

  @media only screen and (max-width: 840px) {
    bottom: -22px;
    margin: 0 -22px -22px;
    padding: 12px 18px;
  }

  @media only screen and (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }

  @media only screen and (max-width: 540px) {
    bottom: -16px;
    margin: 0 -14px -16px;
    padding: 10px 14px;
  }
`;

const FooterNote = styled.div`
  font-size: 0.8125rem;
  color: var(--neutral-500);

  @media only screen and (max-width: 640px) {
    text-align: center;
  }
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;

  @media only screen and (max-width: 640px) {
    justify-content: stretch;
    width: 100%;
  }
`;

const baseBtn = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 40px;
  padding: 0 18px;
  border-radius: 8px;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media only screen and (max-width: 540px) {
    flex: 1;
    min-width: 0;
  }
`;

const CancelBtn = styled.button`
  ${baseBtn}
  min-width: 100px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-700);

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;

const BackBtn = styled.button`
  ${baseBtn}
  min-width: 100px;
  border: 1px solid var(--border);
  background: var(--neutral-0);
  color: var(--neutral-700);

  &:hover:not(:disabled) {
    background: var(--neutral-50);
    border-color: var(--neutral-400);
  }
`;

const NextBtn = styled.button`
  ${baseBtn}
  min-width: 130px;
  border: none;
  background: var(--primary-600);
  color: var(--neutral-0);
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const SubmitBtn = styled.button`
  ${baseBtn}
  min-width: 180px;
  padding: 0 22px;
  border: none;
  background: var(--primary-600);
  color: var(--neutral-0);
  letter-spacing: 0.01em;

  &:hover:not(:disabled) {
    background: var(--primary-700);
  }

  &:active:not(:disabled) {
    transform: translateY(1px);
  }
`;

const SpinIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 18px;
    animation: rotate 1s linear infinite;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(-360deg);
    }
  }
`;
